import { Box, Button, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { ESortDirection, TTableStructure } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import { TFilters, TOrderBy } from 'redux/types';
import { TEuDdpTariff } from 'redux/reducers/euDdp/types';
import { searchTariffsRequest } from 'redux/reducers/euDdp/reducer';
import { selectTariffsSearch, selectTariffsSearchLoading } from 'redux/reducers/euDdp/selectors';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import { tableStructure as defaultTableStructure } from './tableStructure';
import { TariffDialog } from './TariffDialog';

const EDIT_ACTION = 'Edit';

export const EuDdpTariffs: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();
	const [currentTableStructure, setCurrentTableStructure] = useState(defaultTableStructure);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [tariffToEdit, setTariffToEdit] = useState<TEuDdpTariff | null>(null);

	const tariffsData = useSelector(selectTariffsSearch);
	const tariffsLoading = useSelector(selectTariffsSearchLoading);

	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TEuDdpTariff>[]) => {
			setCurrentTableStructure(newTableStructure);
		},
		[],
	);

	const loadTariffs = useCallback(() => {
		const filters = getFiltersFromNavigateParams(
			currentTableStructure,
			queryString.parse(search),
		) as TFilters;

		const params = queryString.parse(search);
		const orderBy: TOrderBy[] = [];
		if (params?.sortIndex) {
			orderBy.push({
				fieldName: defaultTableStructure[+params.sortIndex].selector as string,
				direction:
					String(params.sortDirection) === 'desc' ? ESortDirection.DESC : ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.DESC });

		dispatch(
			searchTariffsRequest({
				pageNumber: params.page ? +params.page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(params?.search ?? ''),
				filters,
				orderBy,
			}),
		);
	}, [currentTableStructure, dispatch, search]);

	useEffect(() => {
		loadTariffs();
	}, [loadTariffs]);

	const handleAddNew = useCallback(() => {
		setTariffToEdit(null);
		setDialogOpen(true);
	}, []);

	const handleAction = useCallback(
		(id: number, action: string) => {
			if (action === EDIT_ACTION) {
				const row = tariffsData?.data?.find((t) => t.id === id) ?? null;
				setTariffToEdit(row);
				setDialogOpen(true);
			}
		},
		[tariffsData],
	);

	const handleDialogClose = useCallback(() => {
		setDialogOpen(false);
		setTariffToEdit(null);
		loadTariffs();
	}, [loadTariffs]);

	return (
		<>
			<TariffDialog open={dialogOpen} onClose={handleDialogClose} tariffToEdit={tariffToEdit} />
			<EasTableWithFilter<TEuDdpTariff>
				tableStructure={currentTableStructure}
				items={tariffsData}
				itemsIsLoading={tariffsLoading}
				handleAction={handleAction}
				onTableStructureChange={handleTableStructureChange}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary">
						Resources / Reference Tables / EU DDP / <b>Tariffs</b>
					</Typography>
					<Button variant="contained" color="primary" size="small" onClick={handleAddNew}>
						Add New Tariff
					</Button>
				</Box>
			</EasTableWithFilter>
		</>
	);
};
